import React, { useEffect, useState } from "react";
import {
  FaClock,
  FaEnvelope,
  FaGlobe,
  FaInstagram,
  FaLinkedinIn,
  FaTwitter,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchContactPage } from "../api/contactPage";
import { createQuery } from "../api/queries";
import { useHomePage } from "../context/HomePageContext";
import {
  contactSidebarBg,
  iconPhone,
  iconEmail,
  iconHospital,
  iconClinic,
} from "../assets/appImages";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  department: "",
  message: "",
};

const DEFAULT_PHONE_SECONDARY = "+91-1126965100";

const DEPARTMENTS = [
  "Knee Replacement",
  "Hip Replacement",
  "Spine Surgery",
  "General Inquiry",
];

const SOCIAL_LINKS = [
  { label: "Twitter", href: "#", Icon: FaTwitter },
  { label: "LinkedIn", href: "#", Icon: FaLinkedinIn },
  { label: "Instagram", href: "#", Icon: FaInstagram },
];

const inputClass =
  "w-full rounded-[8px] border border-[#E4E4E4] bg-white px-4 py-3 text-[14px] text-[#444444] outline-none transition-colors focus:border-brand-purple";

const ContactPage = () => {
  const { setContact } = useHomePage();
  const [contact, setLocalContact] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadContactPage = async () => {
      try {
        const data = await fetchContactPage();
        if (!isMounted) return;

        setLocalContact(data.contact);
        if (data.contact) {
          setContact(data.contact);
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadContactPage();

    return () => {
      isMounted = false;
    };
  }, [setContact]);

  const hospitalHours = contact?.clinic_visit_time || "09:30 AM - 03:00 PM";
  const clinicHours = contact?.hospital_visit_time || "04:00 PM onward";
  const email = contact?.email || "info@dryashgulati.com";
  const phone = contact?.phone_number || "+91-9650193020";
  const hospitalAddress =
    contact?.hospital_address || "Green Park Main, Near Yes Bank, New Delhi 110016";
  const clinicAddress =
    contact?.clinic_address || "E-12 Green Park Main, Near Yes Bank, New Delhi 110016";

  const splitAddress = (value) => {
    if (!value) return [];
    const parts = value.split(",").map((part) => part.trim()).filter(Boolean);
    if (parts.length <= 1) return [value];
    const last = parts[parts.length - 1];
    const rest = parts.slice(0, -1).join(", ");
    return [`${rest},`, last];
  };

  const infoCards = [
    {
      icon: iconPhone,
      title: "Contact Number",
      lines: [phone, DEFAULT_PHONE_SECONDARY].filter(
        (line, index, arr) => line && arr.indexOf(line) === index
      ),
      border: "border-[#D8C8F0]",
      glow: "bg-[#EDE4F8]",
    },
    {
      icon: iconEmail,
      title: "Email Address",
      lines: [email],
      border: "border-[#F5D0D8]",
      glow: "bg-[#FDE8EC]",
    },
    {
      icon: iconHospital,
      title: "Hospital Address",
      lines: splitAddress(hospitalAddress),
      border: "border-[#C8E8D4]",
      glow: "bg-[#E4F6EA]",
    },
    {
      icon: iconClinic,
      title: "Clinic Address",
      lines: splitAddress(clinicAddress),
      border: "border-[#D0D4F5]",
      glow: "bg-[#E8EAF8]",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await createQuery({
        name: form.name.trim(),
        department: form.department,
        phone_number: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });

      toast.success(result?.message || "Query submitted successfully.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err.message || "Unable to submit query. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white pb-16 pt-10 md:pb-20 md:pt-12 lg:pb-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="overflow-hidden rounded-[20px] bg-white shadow-[0_16px_50px_rgba(118,9,130,0.1)]">
          <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <form onSubmit={handleSubmit} className="space-y-4 p-6 md:p-8 lg:p-10">
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your Name"
                className={inputClass}
                required
                disabled={submitting}
              />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Email"
                className={inputClass}
                required
                disabled={submitting}
              />
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Phone Numbers"
                className={inputClass}
                required
                disabled={submitting}
              />
              <select
                name="department"
                value={form.department}
                onChange={handleChange}
                className={`${inputClass} appearance-none ${!form.department ? "text-[#999999]" : ""}`}
                required
                disabled={submitting}
              >
                <option value="" disabled>
                  Select Department
                </option>
                {DEPARTMENTS.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Type Message"
                rows={5}
                className={`${inputClass} resize-none`}
                required
                disabled={submitting}
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-[8px] bg-brand-orange py-3.5 text-[15px] font-semibold text-white shadow-cta-orange transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </form>

            <aside className="relative min-h-[520px] overflow-hidden bg-brand-purple p-6 text-white md:p-8 lg:min-h-full lg:rounded-tr-[20px] lg:p-10">
              {/* Figma: medical staff photo as faded backdrop */}
              <img
                src={contactSidebarBg}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[center_top]"
              />
              {/* Soft purple wash so text stays readable while photo shows through */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-brand-purple/70"
              />

              <div className="relative z-[1]">
                <h2 className="text-[22px] font-bold leading-snug md:text-[24px] w-2/3">
                  Contact Us For Any Informations
                </h2>

                <div className="mt-8 border-t border-white/30 pt-6">
                  <div className="flex items-center gap-3">
                    <FaClock className="text-[18px] shrink-0" aria-hidden="true" />
                    <p className="text-[15px] font-semibold">Opening Hours</p>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 text-[14px] leading-[1.7]">
                    <div>
                      <p className="font-semibold">Hospital -</p>
                      <p className="mt-1 text-white/90">{hospitalHours}</p>
                    </div>
                    <div>
                      <p className="font-semibold">Clinic -</p>
                      <p className="mt-1 text-white/90">{clinicHours}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 border-t border-white/25 pt-6">
                  <div className="flex items-center gap-3">
                    <FaEnvelope className="text-[18px]" aria-hidden="true" />
                    <p className="text-[15px] font-semibold">Email &amp; Phone</p>
                  </div>
                  <div className="mt-4 space-y-1 text-[14px] leading-[1.8] text-white/95">
                    <p>Email: {email}</p>
                    <p>Phone: {DEFAULT_PHONE_SECONDARY}</p>
                    <p>Phone: {phone}</p>
                  </div>
                </div>

                <div className="mt-6 border-t border-white/25 pt-6">
                  <div className="flex items-center gap-3">
                    <FaGlobe className="text-[18px]" aria-hidden="true" />
                    <p className="text-[15px] font-semibold">Follow Us</p>
                  </div>
                  <div className="mt-4 flex items-center gap-2.5">
                    {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                      <a
                        key={label}
                        href={href}
                        aria-label={label}
                        target="_blank"
                        rel="noreferrer"
                        className="flex h-9 w-9 items-center justify-center rounded-[4px] bg-white text-[#565ACF] transition-opacity hover:opacity-90"
                      >
                        <Icon className="text-[14px]" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:mt-10 lg:grid-cols-4 lg:gap-6">
          {infoCards.map(({ icon, title, lines, border, glow }) => (
            <article
              key={title}
              className={`rounded-[14px] border bg-white px-5 py-8 text-center shadow-[0_4px_20px_rgba(0,0,0,0.04)] ${border}`}
            >
              <div className="relative mx-auto flex h-16 w-16 items-center justify-center">
                <span
                  aria-hidden="true"
                  className={`absolute inset-0 rounded-full opacity-80 blur-[1px] ${glow}`}
                />
                <img
                  src={icon}
                  alt=""
                  aria-hidden="true"
                  className="relative z-[1] h-12 w-12 object-contain"
                />
              </div>
              <h3 className="mt-5 text-[17px] font-bold text-[#232D6E] md:text-[18px]">{title}</h3>
              <div className="mt-3 space-y-1">
                {lines.map((line) => (
                  <p key={line} className="text-[13px] leading-[1.6] text-[#666666] md:text-[14px]">
                    {line}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
